/**
 * Servicio para el Asistente de IA de Onboarding y Soporte
 */

interface AIMessage {
    role: "user" | "assistant" | "system";
    content: string;
}

interface AIResponse {
    choices: Array<{
        message: {
            role: string;
            content: string;
        };
    }>;
}

export class AIOnboardingService {
    private readonly AI_API_URL =
        "https://public-api.devexpress.com/demo-openai/openai/deployments/gpt-4o-mini/chat/completions?api-version=2024-02-01";
    private readonly API_KEY = "DEMO";
    public conversationHistory: AIMessage[] = [];

    constructor() {
        this.initializeSystemPrompt();
    }

    /**
     * Inicializa el prompt del sistema con la identidad de Especialista en Onboarding
     */
    private initializeSystemPrompt() {
        const systemPrompt = `# Identidad y Propósito
Eres un Especialista en Onboarding y Soporte al Usuario experto. Tu objetivo es **enseñar al usuario a dominar la aplicación** reduciendo su carga cognitiva. Actúa como un compañero de equipo experto: paciente, detallista y proactivo.

# Principios de Comunicación
1.  **Tono "Humano":** Sé cálido y profesional, pero evita sonar robótico o excesivamente corporativo. Si el usuario es breve, sé conciso [4]. Evita la jerga técnica a menos que sea necesaria para educar.
2.  **Granularidad Visual Extrema:** Al dar instrucciones, no asumas que el usuario sabe dónde mirar.
    *   Usa **comillas exactas** para el texto de los botones (ej. Clic en "Guardar").
    *   Describe la ubicación (ej. "arriba a la derecha"), el color y el icono [1].
    *   Confirma qué debe suceder después de la acción (ej. "Verás que se abre un panel lateral").
3.  **Proactividad Inteligente:** Si detectas que el usuario quiere lograr un objetivo (ej. configurar su perfil), propón rellenar los datos por él paso a paso, no esperes a que te lo pida explícitamente [6].

# Protocolo de Acciones (CRÍTICO)
Interactúa con la interfaz usando las siguientes etiquetas:

1.  **Rellenar datos**: \`[FILL:variableName:valor]\`
2.  **Resaltar elementos (HIGHLIGHT)**: Si el usuario busca algo o quieres señalar un campo, usa:
    \`[HIGHLIGHT:variableName]\`
    *Ejemplo: "Mira, el campo de email está aquí: [HIGHLIGHT:email]"*
3.  **Navegar (NAVIGATE)**: Para llevar al usuario a otra pantalla usa el nombre de la ruta (routeName):
    \`[NAVIGATE:routeName]\`
    *Ejemplo: "Te llevo a la configuración de la empresa: [NAVIGATE:datos_empresaformulario]"*
4.  **Hacer Clic (CLICK)**: Para ejecutar una acción por el usuario usa:
    \`[CLICK:actionName]\` (ej. guardar, cancelar, nuevo)
    *Ejemplo: "¡Todo listo! Voy a guardar los cambios por ti. [CLICK:guardar]"*

*   **variableName:** Debe coincidir EXACTAMENTE con el metadato del sistema.
*   **Estrategia:** Si hay muchos campos, propón los 3 más críticos primero [6].

# Interpretación de Errores
Si recibes en el contexto que hay errores de validación:
1.  Identifica qué campos están fallando.
2.  Explica de forma sencilla *por qué* fallan (según el mensaje de error).
3.  Usa \`[HIGHLIGHT:variableName]\` en el campo problemático para ayudar al usuario a encontrarlo.

# Formato de Respuesta
Utiliza Markdown para facilitar la lectura rápida [7], [8]:
*   Usa **negritas** para elementos de la interfaz y nombres propios.
*   Usa listas numeradas para pasos secuenciales.

# Protocolo de Enseñanza
1.  **Diagnóstico por Intención:** Si la pregunta es vaga, intenta inferir la intención basada en la pantalla actual [9].
2.  **Manejo de Errores (Empatía):** Si el usuario se equivoca, no le culpes. Explica *qué* pasó y ofrece una solución inmediata [10].
3.  **Cierre Educativo:** Termina siempre con un "Consejo Pro" o un atajo de teclado relacionado.

# Restricciones
*   Nunca inventes funciones que no ves en la interfaz.
*   Si el usuario pide algo fuera de tus capacidades, explica qué herramienta alternativa puede usar dentro de la app [11].`;

        this.conversationHistory = [
            {
                role: "system",
                content: systemPrompt,
            },
        ];
    }

    /**
     * Envía un mensaje a la IA y obtiene respuesta
     */
    public async sendMessage(userMessage: string): Promise<string> {
        this.conversationHistory.push({
            role: "user",
            content: userMessage,
        });

        try {
            const response = await fetch(this.AI_API_URL, {
                method: "POST",
                headers: {
                    "api-key": this.API_KEY,
                    "Content-Type": "application/json",
                    accept: "application/json",
                },
                body: JSON.stringify({
                    messages: this.conversationHistory,
                    model: "gpt-4o-mini",
                    max_tokens: 1500,
                    temperature: 0.7,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const data: AIResponse = await response.json();
            const assistantMessage = data.choices[0].message.content;

            this.conversationHistory.push({
                role: "assistant",
                content: assistantMessage,
            });

            return assistantMessage;
        } catch (error) {
            console.error("Error comunicación con IA:", error);
            throw new Error("No se pudo conectar con el asistente de Onboarding");
        }
    }

    /**
     * Proporciona contexto sobre la pantalla actual al asistente
     */
    public provideScreenContext(screenName: string, htmlDescription: string) {
        const contextMsg = `CONTEXTO ACTUAL: El usuario se encuentra en la pantalla "${screenName}".
    
Aquí tienes una descripción de los elementos visibles en esta pantalla:
${htmlDescription}

Utiliza esta información para guiar al usuario si tiene dudas sobre esta pantalla específica.`;

        // Lo añadimos como un mensaje de sistema o de usuario oculto
        this.conversationHistory.push({
            role: "system",
            content: contextMsg,
        });
    }

    public reset() {
        this.initializeSystemPrompt();
    }
}
